import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const accountLinks = [
  { title: 'Managing your account settings', href: '#account-settings' },
  { title: 'How to update your profile information', href: '#update-profile' },
  { title: 'Changing your email address', href: '#change-email' },
  { title: 'Resetting your password', href: '#reset-password' },
  { title: 'Account did not activate', href: '#account-not-activated' },
  { title: 'Deleting your account', href: '#delete-account' },
];

const billingLinks = [
  { title: 'Managing your invoices', href: '#managing-invoices' },
  { title: 'How to manage your billing in SweetPilot App', href: '#manage-billing' },
  { title: '14-Day Money-Back Guarantee', href: '#money-back-guarantee' },
  { title: 'How to apply a coupon code', href: '#apply-coupon' },
  { title: 'Understanding SweetPilot discounts', href: '#discounts' },
  { title: 'Why is VAT collected?', href: '#vat-collected' },
  { title: 'Workspace credits', href: '#workspace-credits' },
  { title: 'Plans and pricing', href: '/pricing' },
];

const subscriptionLinks = [
  { title: 'How to upgrade your plan', href: '#upgrade-plan' },
  { title: 'How to downgrade your plan', href: '#downgrade-plan' },
  { title: 'How to cancel your subscription', href: '#cancel-subscription' },
  { title: 'Switching between monthly and yearly billing', href: '#billing-cycle' },
  { title: 'What happens when my subscription expires?', href: '#subscription-expired' },
];

const creditsLinks = [
  { title: 'Understanding credits and usage', href: '#understanding-credits' },
  { title: 'How to buy additional credits', href: '#buy-credits' },
  { title: 'Credit pricing and packages', href: '#credit-pricing' },
  { title: 'What happens when I run out of credits?', href: '#out-of-credits' },
];

const paymentLinks = [
  { title: 'Accepted payment methods', href: '#payment-methods' },
  { title: 'How to update your payment method', href: '#update-payment' },
  { title: 'Payment failed - what to do', href: '#payment-failed' },
  { title: 'Refund policy', href: '/refund-policy' },
];

const specialOffersLinks = [
  { title: 'Student plan eligibility', href: '#student-plan' },
  { title: 'Gift cards - How to purchase and redeem', href: '#gift-cards' },
  { title: 'Enterprise and custom plans', href: '#enterprise-plans' },
];

const faqLinks = [
  { title: 'Account & Billing FAQ', href: '#billing-faq' },
  { title: 'Troubleshooting billing issues', href: '#troubleshooting' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function AccountBillingDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Account & Billing
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Account & Billing
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Manage your account, team, and subscription.
        </p>
      </motion.div>

      {/* Account Management */}
      <SectionCard
        title="Account Management"
        links={accountLinks}
        isDark={isDark}
        delay={0.1}
      />

      <div className="h-6" />

      {/* Billing */}
      <SectionCard
        title="Billing"
        links={billingLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* Subscription */}
      <SectionCard
        title="Subscription"
        links={subscriptionLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* Credits */}
      <SectionCard
        title="Credits"
        links={creditsLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-6" />

      {/* Payment */}
      <SectionCard
        title="Payment"
        links={paymentLinks}
        isDark={isDark}
        delay={0.5}
      />

      <div className="h-6" />

      {/* Special Offers */}
      <SectionCard
        title="Special Offers"
        links={specialOffersLinks}
        isDark={isDark}
        delay={0.6}
      />

      <div className="h-6" />

      {/* FAQ & Troubleshooting */}
      <SectionCard
        title="Help & Support"
        links={faqLinks}
        isDark={isDark}
        delay={0.7}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">💳</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
